import { createSignal, untrack } from "solid-js";

/**
 * Focus around the layers built on Kobalte: dialogs, sheets, popovers, selects.
 *
 * Kobalte gives focus back on close only to a `Trigger` it rendered itself, and
 * most of our dialogs open from a signal instead, so closing them dropped focus
 * on `body`. Its non-modal popovers and selects are portalled to the end of
 * `body` and trap nothing, and while one is open it pauses the trap of the
 * dialog around it, so Tab past the popover's last control left the page.
 */

const TABBABLE =
  'a[href], button, input, select, textarea, summary, iframe, [contenteditable]:not([contenteditable="false"]), [tabindex]';

/**
 * The elements Tab reaches inside `root`, in DOM order (positive `tabindex`
 * ordering is ignored: nothing here uses it). Skips focus guards: Kobalte's own
 * trap sentinels and ours both carry `data-focus-trap`, which is also what
 * keeps Kobalte's open autofocus off them.
 */
function tabbablesIn(root: Element): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(TABBABLE)].filter(
    (el) =>
      el.tabIndex >= 0 &&
      !el.hasAttribute("data-focus-trap") &&
      !el.matches(':disabled, input[type="hidden"]') &&
      !el.closest("[hidden], [inert]") &&
      // jsdom has no layout and no `checkVisibility`: there, count it visible.
      (el.checkVisibility?.({ visibilityProperty: true }) ?? true),
  );
}

/**
 * Where Tab goes after `reference`, skipping `layer`, the portalled content
 * being left. The search stays inside the dialog that holds the reference, and
 * past its last control wraps to its first, as the dialog's own trap does.
 */
function tabbableAfter(reference: HTMLElement, layer: Element) {
  const scope =
    reference.closest('[role="dialog"], [role="alertdialog"]') ??
    reference.ownerDocument.body;
  const candidates = tabbablesIn(scope).filter((el) => !layer.contains(el));
  const next = candidates.find(
    (el) =>
      !reference.contains(el) &&
      reference.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING,
  );
  return next ?? candidates[0];
}

/**
 * The focused element, as the one to give focus back to later. A menu item
 * leaves with its menu, so for one this is the menu's trigger, where Kobalte
 * itself returns focus when the menu closes: it labels the menu with it.
 */
function focusedInvoker(): HTMLElement | null {
  let el = document.activeElement;
  let menu = el?.closest('[role="menu"][aria-labelledby]');
  while (menu) {
    const trigger = document.getElementById(
      menu.getAttribute("aria-labelledby") ?? "",
    );
    if (!trigger || menu.contains(trigger)) break;
    el = trigger;
    menu = el.closest('[role="menu"][aria-labelledby]');
  }
  return el instanceof HTMLElement && el !== document.body ? el : null;
}

/**
 * Remembers the element focused when a layer opens and gives focus back to it
 * when the layer closes, if nothing else has it by then. One per layer content.
 *
 * Kobalte already does this for a layer opened by its own `Trigger`, and its
 * handler runs after ours, so where there is one the trigger still wins. When
 * the element is gone by then — the item of a popover that closed in between —
 * focus stays where Kobalte leaves it: the opener has to hand focus to
 * something that stays, as the ATS drawer's status popover does.
 */
export function createFocusReturn() {
  let invoker: HTMLElement | null = null;
  const connected = () => (invoker?.isConnected ? invoker : null);

  return {
    /**
     * For `onOpenAutoFocus`, which Kobalte fires before it moves focus into
     * the layer — and, through the call site's own handler, inside one of its
     * effects: keep the call in `untrack`.
     */
    remember() {
      invoker = focusedInvoker();
    },
    /** The remembered element, while it is still in the page. */
    invoker: connected,
    /** For `onCloseAutoFocus`, after the call site's own handler. */
    restore(event: Event) {
      const target = connected();
      invoker = null;
      // Prevented by the call site, or by Kobalte because focus already moved
      // to another focusable element outside the layer.
      if (!target || event.defaultPrevented) return;
      const active = document.activeElement;
      if (active && active !== document.body) return;
      target.focus({ preventScroll: true });
    },
  };
}

/**
 * The autofocus handlers of a dialog or sheet content: the call site's own,
 * plus `createFocusReturn`, so a dialog opened from a signal, with no
 * `Trigger`, gives focus back to whatever opened it instead of to `body`.
 */
export function withFocusReturn(handlers: {
  onOpenAutoFocus?: (event: Event) => void;
  onCloseAutoFocus?: (event: Event) => void;
}) {
  const focusReturn = createFocusReturn();

  return {
    onOpenAutoFocus: (event: Event) =>
      untrack(() => {
        focusReturn.remember();
        handlers.onOpenAutoFocus?.(event);
      }),
    onCloseAutoFocus: (event: Event) => {
      handlers.onCloseAutoFocus?.(event);
      focusReturn.restore(event);
    },
  };
}

/**
 * State for a popover used as an action menu — the ATS drawer's status pill
 * and overflow, an offer card's «Azioni»: picking an item closes it and hands
 * focus back to its trigger before running the action. A dialog or sheet the
 * action opens then returns focus to that trigger when it closes, instead of
 * to `body`: `withFocusReturn` gives focus back to whatever had it at open,
 * and an item of a closing popover is gone by then.
 *
 * Closing first is also what lets such a popover be `topLayer` inside a
 * dialog: none stays open over the layer its item opens.
 */
export function createActionPopover() {
  const [open, setOpen] = createSignal(false);
  let trigger: HTMLButtonElement | undefined;

  return {
    open,
    setOpen,
    setTrigger: (el: HTMLButtonElement) => {
      trigger = el;
    },
    choose: (action: () => void) => {
      setOpen(false);
      trigger?.focus();
      action();
    },
  };
}

/**
 * A guard at one edge of a portalled layer that does not trap focus — a
 * non-modal popover, a select's list — that keeps Tab in page order, the way
 * a native `<select>` does:
 * - `end`: Tab past the last control moves to the control after `reference`
 *   (the trigger, or whatever opened the layer), and the layer closes, since
 *   focus left it;
 * - `start`: Shift+Tab before the first control moves to `reference`. Focus
 *   arriving from anywhere else — the content itself, or a control before the
 *   portal — goes on to the first control.
 * Without a reference, Tab cycles inside the layer.
 *
 * It has to be a direct child of the layer's content element.
 */
export function FocusGuard(props: {
  edge: "start" | "end";
  reference: () => HTMLElement | null | undefined;
}) {
  const onFocus = (event: FocusEvent) => {
    const guard = event.currentTarget as HTMLElement;
    const layer = guard.parentElement;
    if (!layer) return;
    const inside = tabbablesIn(layer);
    const reference = props.reference();
    const ref = reference?.isConnected ? reference : null;
    const from = event.relatedTarget;
    const leavingBackwards =
      props.edge === "start" &&
      from instanceof Node &&
      from !== layer &&
      layer.contains(from);

    const target = leavingBackwards
      ? (ref ?? inside.at(-1))
      : props.edge === "start" && inside.length > 0
        ? inside[0]
        : ((ref && tabbableAfter(ref, layer)) ?? inside[0]);
    target?.focus();
  };

  return (
    // A focus guard is a Tab stop with a focus handler by definition, like
    // Kobalte's own trap sentinels, and nothing for assistive tech to read.
    // biome-ignore lint/a11y/noNoninteractiveTabindex: see above
    // biome-ignore lint/a11y/noStaticElementInteractions: see above
    <span data-focus-trap="" tabIndex={0} class="sr-only" onFocus={onFocus} />
  );
}
